export const LEGAL = {
  terms: { title: "Terms of use", body: `**Last updated: June 2026.** These terms apply to your purchase and use of *Bedside to Remote: The Nurse's Transition Course* ("the course").

## What you are buying
A one-time purchase gives one person a personal, non-transferable licence to access the course content, downloads and AI Career Coach for as long as we continue to offer the course. Sharing logins or redistributing course materials is not permitted.

## Access dates (promotional plans)
During the promotion (ending 31/12/2026) the course was $250 and is offered as: $197 with full access 3 days after payment, $97 with full access 1 week after payment, or $57 with full access 2 weeks after payment. You can create your account immediately after payment; course content opens at the stated time and we email you when it does.

## Account security and sharing
Your access is tied to the email address used at purchase and to the device you set your account up on. Signing in on a new device requires a verification code sent to that email, and a maximum of two devices can be linked. Attempts to share an account may lead to suspension without refund.

## Educational guidance only
This course is educational career guidance. It does not guarantee employment and does not provide medical, legal or financial advice. Salary and fee figures are approximate and may be out of date. Always verify licensure, certification and employer requirements with the official source.

## AI features
Videos are presented by an AI-generated presenter, and the Career Coach is an AI assistant. AI output can contain mistakes. You are responsible for checking anything you use in an application. Do not paste patient information or private personal identifiers into the coach.

## Fair use
The AI Career Coach is rate-limited to keep the service available for everyone. We may suspend access for abuse, fraud or chargebacks.

## Payments and refunds
Payments are processed by Stripe. **All sales are final and no refunds are given**, because full access to the digital course content starts immediately after payment. See the Refund policy.

## Liability
To the extent permitted by law, we are not liable for indirect or consequential losses, including lost earnings or employment outcomes.

## Contact
Questions about these terms: reply to any course email.` },
  privacy: { title: "Privacy policy", body: `**Last updated: June 2026.**

## What we collect
- **Account data**: your email address and name (from checkout).
- **Payment data**: processed by Stripe. We receive a payment reference, amount and status; **we never see or store your card number**.
- **Course data**: progress, exercise answers, and AI Career Coach conversations and ratings.
- **Technical data**: a sign-in cookie and basic server logs.

## How we use it
To provide course access, send sign-in and purchase emails, show your progress, operate and improve the AI Career Coach, prevent fraud, and meet legal obligations. We do not sell your data and we do not use advertising trackers.

## AI processing
Coach messages are sent to our AI model provider to generate replies. Please never include patient information or private identifiers. You can clear your coach history at any time from the coach panel.

## Processors
Stripe (payments), our email delivery provider (transactional emails), our cloud hosting and storage providers, and our AI model provider.

## Retention and your rights
We keep account and purchase records as long as needed for access and legal/tax purposes. You can ask us to access, correct or delete your data (subject to records we must keep). If you are in the UK or EU, you also have rights under UK GDPR / GDPR, including the right to complain to your data protection authority.

## Contact
Reply to any course email to make a privacy request.` },
  refund: { title: "Refund policy", body: `**Last updated: June 2026.**

## All sales are final
**We do not offer refunds.** The course is digital content, and full access to every module, download and the AI Career Coach is granted immediately after payment.

- Before you buy, you can watch the full welcome video and read the complete module outline and FAQ to decide whether the course is right for you.
- At checkout you confirm that you want immediate access and that you understand all sales are final. If you are in the UK or EU, this means you agree that your 14-day right to cancel ends once access begins.
- **Duplicate or accidental charges:** if you are charged twice for the same course, contact us with your purchase email and we will correct the duplicate charge.
- **Payment errors:** if a charge was made in error or without your authorisation, contact us and we will investigate.
- Nothing in this policy limits rights you have under consumer law that cannot be excluded.` },
  disclaimer: { title: "Disclaimer", body: `**This course is educational career guidance. It does not guarantee employment and does not provide medical, legal or financial advice.**

- No income promises, job guarantees or interview guarantees are made or implied.
- Salary ranges, certification fees and eligibility rules are **approximate**, drawn from published sources at the time of writing, and can change. Verify with the certifying body, your state board of nursing (or the NMC in the UK) and the employer.
- Employer names are research starting points, not endorsements or confirmation that they are hiring.
- Clinical examples are simplified teaching scenarios for career-skills practice, not clinical guidance.` },
  ai: { title: "AI disclosure", body: `We use AI in two places, and we label both clearly.

## AI-generated video presenter
The course videos are presented by an AI-generated avatar, "the Remote Nursing Career Coach". **It is not a real nurse or a real person.** Scripts are written from the course content and reviewed before videos are generated. Each video is labelled "AI-generated presenter" and has captions and a transcript.

## AI Career Coach
The chat assistant is powered by a large language model. It uses the course content as its main knowledge base and cites the module it draws from. It can make mistakes, may be out of date on fees, exam dates and licensure rules, and does not give clinical, legal or financial advice. Never paste patient information or private personal identifiers.` },
  cookies: { title: "Cookie policy", body: `We use **essential cookies only**.

| Cookie | Purpose | Duration |
|---|---|---|
| access_token | Keeps you signed in to the course | 7 days |
| Stripe cookies | Set by Stripe on the checkout page for fraud prevention | Set by Stripe |
| b2r_cookie_notice_v1 (local storage) | Remembers that you dismissed this notice | Until cleared |

We do not use advertising, analytics-profiling or cross-site tracking cookies.` },
};
